/** Choose an existing event or type a new one to upload into. */
import { useEffect, useId, useRef, useState } from 'react';
import { IconChevronDown, IconPlus } from '@tabler/icons-react';
import { useEvents } from '../../hooks/useEvents.ts';
import { sanitizeEventName } from '../../lib/keys.ts';
import { UNCATEGORIZED_EVENT } from '../../config/awsConfig.ts';
import { cn } from '../../lib/cn.ts';

interface EventPickerProps {
  value: string;
  onChange: (event: string) => void;
}

export function EventPicker({ value, onChange }: EventPickerProps) {
  const { data: events } = useEvents();
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState('');
  const selectId = useId();
  const newInputRef = useRef<HTMLInputElement>(null);

  // Focus the new-event input when it appears (replaces autoFocus).
  useEffect(() => {
    if (creating) newInputRef.current?.focus();
  }, [creating]);

  const names = (events ?? [])
    .map((e) => e.name)
    .filter((n) => n !== UNCATEGORIZED_EVENT)
    .sort((a, b) => a.localeCompare(b));

  const commitNew = () => {
    const safe = sanitizeEventName(draft);
    if (safe) {
      onChange(safe);
      setCreating(false);
      setDraft('');
    }
  };

  return (
    <div className="space-y-2">
      <label
        htmlFor={selectId}
        className="block text-sm font-medium text-fg-2"
      >
        Event
      </label>
      {creating ? (
        <div className="flex gap-2">
          <input
            ref={newInputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitNew();
              if (e.key === 'Escape') setCreating(false);
            }}
            // Commit on blur so a mobile user who types a name and taps the
            // dropzone (instead of the Add button) still lands in the right
            // event — previously the draft was lost silently and files went
            // to Uncategorized.
            onBlur={commitNew}
            placeholder="New event name (e.g. Goa Trip 2024)"
            className="border border-line bg-surface h-10 flex-1 rounded-xl px-3 text-sm outline-none placeholder:text-muted"
          />
          <button
            onClick={commitNew}
            className="rounded-xl bg-accent px-4 text-sm font-medium text-white"
          >
            Add
          </button>
        </div>
      ) : (
        <div className="flex gap-2">
          <div className="border border-line bg-surface relative flex-1 rounded-xl">
            <select
              id={selectId}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className="h-10 w-full appearance-none bg-transparent px-3 pr-9 text-sm outline-none"
            >
              {value && !names.includes(value) && value !== UNCATEGORIZED_EVENT && (
                <option value={value}>{value} (new)</option>
              )}
              <option value={UNCATEGORIZED_EVENT}>Uncategorized</option>
              {names.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
            <IconChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
          </div>
          <button
            onClick={() => setCreating(true)}
            className={cn(
              'inline-flex h-10 items-center gap-1 rounded-xl px-3 text-sm font-medium',
              'border border-line bg-surface hover:bg-grid',
            )}
          >
            <IconPlus className="h-4 w-4" /> New
          </button>
        </div>
      )}
    </div>
  );
}
